/**
 * Done-for-You purchase card — fan-portal facing.
 *
 * Flow: anonymous fan fills out a form (email, name, YouTube URL, notes) →
 * POST /api/dfy/start-checkout → window.location = stripe url. On return,
 * fan-portal carries ?dfy_session=<id> which polls /api/dfy/checkout-status.
 *
 * Honest UX: this card explains the offer ("we hand-build your twin") + the
 * pricing model ($1000 today + $10/mo activates after delivery) BEFORE we
 * ask for any data, so no one is surprised by what they're paying for.
 */
import { useEffect, useState } from "react";
import { Crown, Sparkle, ArrowRight, Lightbulb, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FALLBACK_PRICE_CENTS = 100000;

export default function DfyPurchaseCard({ priceCents: priceCentsProp, referrerCode = null, compact = false }) {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [livePriceCents, setLivePriceCents] = useState(null);
  const [liveCreatorFeeCents, setLiveCreatorFeeCents] = useState(null);
  // Oct 2026 (Randy) — the whole offering is behind one switch next to the
  // price slider in Pricing Lab. `null` means "haven't asked yet", so the card
  // paints nothing until we know rather than flashing a product that may be
  // retired. Anything other than a clear yes keeps it hidden.
  const [enabled, setEnabled] = useState(null);
  const priceCents = priceCentsProp ?? livePriceCents ?? FALLBACK_PRICE_CENTS;
  const creatorFeeCents = liveCreatorFeeCents ?? 1000;

  // Fetch live DFY price on mount so the card shows whatever the super-admin
  // slider is currently set to. Falls back gracefully if the endpoint fails.
  useEffect(() => {
    api.get("/dfy/checkout-info")
      .then((r) => {
        setEnabled(!!r.data.enabled);
        setLivePriceCents(r.data.price_cents);
        if (r.data.creator_monthly_fee_cents != null) setLiveCreatorFeeCents(r.data.creator_monthly_fee_cents);
      })
      .catch(() => { setEnabled(false); });
  }, []);
  const [form, setForm] = useState({
    buyer_email: "", buyer_name: "", buyer_youtube_url: "", buyer_notes: "",
  });

  const submit = async () => {
    if (!form.buyer_email || !form.buyer_email.includes("@")) {
      toast.error("Valid email required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/dfy/start-checkout", {
        ...form,
        referrer_code: referrerCode,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      setSubmitting(false);
      toast.error(e.response?.data?.detail || "Could not start checkout.");
    }
  };

  return (
    enabled !== true ? null :
    <div className={`rounded-xl bg-gradient-to-br from-[#FDFCF8] to-[#F0F1EE] border-2 border-[#C16E5A]/30 ${compact ? "p-3" : "p-6"} relative overflow-hidden`} data-testid="dfy-purchase-card">
      <div className={`absolute top-3 right-3 ${compact ? "opacity-[0.06]" : "opacity-10"}`}><Crown size={compact ? 48 : 80} weight="duotone" /></div>
      <div className="relative z-10">
        <div className={`text-[10px] uppercase tracking-[0.3em] text-[#C16E5A] ${compact ? "mb-1" : "mb-2"} flex items-center gap-2`}>
          <Sparkle size={12} weight="duotone" /> Done-for-You · Curated by our team
        </div>
        <h3 className={`font-serif-display ${compact ? "text-lg" : "text-2xl sm:text-3xl"} leading-tight text-[#1C1B1A]`}>
          We hand-build your digital twin from your YouTube content.
        </h3>
        {!compact && (
        <p className="text-sm text-[#3A4A3E] mt-2 max-w-2xl">
          Skip the setup. Our team (or a vetted Creator Pro operator) takes your videos, transcripts, descriptions,
          comments, and product links — and curates them into your twin. You log in to a finished product.
        </p>
        )}
        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-2 ${compact ? "mt-2" : "mt-4"}`}>
          <div className={`flex items-start gap-2 ${compact ? "text-[11px]" : "text-xs"} text-[#1C1B1A]`}>
            <Lightbulb size={12} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
            <span><strong>${(priceCents / 100).toFixed(0)} today</strong> — one-time, hand-crafted by humans</span>
          </div>
          <div className={`flex items-start gap-2 ${compact ? "text-[11px]" : "text-xs"} text-[#1C1B1A]`}>
            <Sparkle size={12} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
            <span><strong>${(creatorFeeCents / 100).toFixed(creatorFeeCents % 100 === 0 ? 0 : 2)}/mo activates after delivery</strong> — same price as everyone else</span>
          </div>
          <div className={`flex items-start gap-2 ${compact ? "text-[11px]" : "text-xs"} text-[#1C1B1A]`}>
            <ShieldCheck size={12} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
            <span>Operator-built. We verify before handoff.</span>
          </div>
        </div>

        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            data-testid="dfy-show-form-btn"
            className={`${compact ? "mt-3 px-4 py-2 text-xs" : "mt-5 px-6 py-3 text-sm"} bg-[#C16E5A] text-white hover:bg-[#A85E4A] rounded-md flex items-center gap-2 font-medium`}
          >
            Get a curated twin · ${(priceCents / 100).toFixed(0)}
            <ArrowRight size={compact ? 13 : 16} />
          </button>
        ) : (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="dfy-form">
            <input
              type="email" placeholder="Your email"
              value={form.buyer_email}
              onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
              data-testid="dfy-email"
              className="bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
            <input
              type="text" placeholder="Your name"
              value={form.buyer_name}
              onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
              data-testid="dfy-name"
              className="bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
            <input
              type="url" placeholder="Your YouTube channel URL"
              value={form.buyer_youtube_url}
              onChange={(e) => setForm({ ...form, buyer_youtube_url: e.target.value })}
              data-testid="dfy-youtube"
              className="sm:col-span-2 bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
            <textarea
              placeholder="Anything specific you want emphasized in your twin? (optional)"
              value={form.buyer_notes}
              onChange={(e) => setForm({ ...form, buyer_notes: e.target.value })}
              data-testid="dfy-notes"
              rows={2}
              className="sm:col-span-2 bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
            <div className="sm:col-span-2 flex items-center gap-2">
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="dfy-submit-btn"
                className="bg-[#C16E5A] text-white hover:bg-[#A85E4A] px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-60 flex items-center gap-2"
              >
                {submitting ? "Opening Stripe…" : <>Continue to checkout · ${(priceCents / 100).toFixed(0)} <ArrowRight size={14} /></>}
              </button>
              <button
                onClick={() => setShowForm(false)}
                disabled={submitting}
                className="text-xs text-[#6B6A68] hover:text-[#1C1B1A] underline-offset-2 hover:underline"
              >
                Cancel
              </button>
            </div>
            <p className="sm:col-span-2 text-[10px] text-[#6B6A68] mt-1">
              Stripe checkout. After payment we'll get to work — typical delivery 7–14 business days.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
